import { isEqual } from "lodash";
import * as React from "react";
import { FieldComponentProps } from "../../Redux/Form/components/Field";
import { WithEditModeComponent, WithEditModeProps } from "../../sharedHOC/withEditMode/withEditMode";
import type { Popover } from "../popover";

export type UpdateModel<T, K extends keyof T> = {
    name: K;
    value: T[K];
};

export type UpdateAction<T = any> = <K extends keyof T>(model: UpdateModel<T, K>) => any;

function isPromise(promise: any): promise is JQuery.Promise<any, any, any> {
    return promise && promise.always;
}

export type WithInlineEditProps = Pick<InlinedComponentProps, "alwaysEditMode"> & {
    updateAction?: UpdateAction;
    isRequired?: boolean;
    saveOnChange?: boolean;
    treatNullAsZero?: boolean;
    treatAsEmpty?: (val: any) => boolean;
};

export type InlinedComponentProps<TComponentProps = {}> = TComponentProps &
    Pick<WithEditModeProps, "updateAction" | "cancelAction" | "alwaysEditMode" | "ignoreEnterEscInEditMode"> & {
        onChangeAction?: FieldComponentProps["onChange"];
        onToggle?: React.ComponentProps<typeof Popover>["onToggle"];
        prevValue?: any;
    };

export const withInlineEdit = <P extends InlinedComponentProps>(
    UnwrappedComponent: React.ComponentType<P>,
    defaultProps?: Partial<FieldComponentProps & P & WithInlineEditProps>
) => {
    type Props = FieldComponentProps & Omit<P, "updateAction" | "value"> & WithInlineEditProps;

    return class WithInlineEdit extends React.Component<Props> {
        static defaultProps = defaultProps;

        private withEditModeRef: WithEditModeComponent;
        private needRunUpdate: boolean;

        withEditModeRefSetter = (comp: WithEditModeComponent) => {
            this.withEditModeRef = comp;
        };

        cancelAction = () => {
            if (
                (this.props.prevValue !== this.props.value || !this.props.alwaysEditMode) &&
                !this.props.ignoreEnterEscInEditMode
            ) {
                this.props.onChange(this.props.prevValue);
                return true; /* Value was reverted */
            }
        };

        updateAction = () => !this.props.saveOnChange && this.updateActionImpl(this.props.value);

        updateActionImpl(value: any) {
            const { name, prevValue, isRequired, validation, textValue, treatAsEmpty } = this.props;
            const invalid = validation && !validation.isValid;

            if (invalid) {
                // mark form field as touched for showing error message.
                this.props.onBlur();
            }

            let fixedValue = value;
            if (typeof value === "string") {
                fixedValue = value.trim();
            }

            if (
                isRequired &&
                ((treatAsEmpty && treatAsEmpty(fixedValue)) || (WithInlineEdit.isEmptyValue(fixedValue) && !textValue))
            ) {
                this.cancelAction();
                return;
            }

            if (value !== fixedValue) {
                this.props.onChange(fixedValue);
            }

            if (!WithInlineEdit.isValueChanged(prevValue, fixedValue) || invalid) {
                return;
            }

            if (this.props.updateAction) {
                const promise = this.props.updateAction({ name, value: fixedValue });

                if (isPromise(promise)) {
                    this.props.onUpdateStarted();
                    promise.always(() => {
                        this.props.onUpdateCompleted();
                    });
                }
            }
        }

        onChange = (value: any, textValue?: string) => {
            if (this.props.saveOnChange) {
                // postpone update to next render because we need
                // wait for running validation with new value
                // this.props.onChange should trigger rendering of this component
                this.needRunUpdate = true;
            }

            this.props.onChange(value, textValue);

            if (this.props.saveOnChange) {
                if (!this.props.alwaysEditMode && this.withEditModeRef) {
                    this.withEditModeRef.closeEditMode(true);
                }
            }
        };

        inlinePopupSelectOnToggleOpen = (isOpen: boolean) => {
            if (!isOpen && this.withEditModeRef) {
                if (!this.props.saveOnChange || this.needRunUpdate) {
                    this.withEditModeRef.closeEditMode();
                } else {
                    this.withEditModeRef.escapeEditMode();
                }
            }
        };

        render() {
            const {
                // exclude props that are overridden in this component
                updateAction,
                onChange,
                ...restProps
            } = this.props;

            const props = {
                ...restProps,
                onToggle: this.inlinePopupSelectOnToggleOpen,
            };

            const isFilled =
                (this.props.value != null &&
                    this.props.value !== "" &&
                    this.props.value?.length !== 0 &&
                    !(this.props.treatNullAsZero && this.props.value === 0)) ||
                !!this.props.textValue;

            return (
                <UnwrappedComponent
                    {...(props as any)}
                    isFilled={isFilled}
                    // for text inputs
                    onChangeAction={this.onChange}
                    updateAction={this.updateAction}
                    cancelAction={this.cancelAction}
                    withEditModeRef={this.withEditModeRefSetter}
                />
            );
        }

        componentDidUpdate() {
            if (this.needRunUpdate) {
                this.updateActionImpl(this.props.value);
                this.needRunUpdate = false;
            }
        }

        private static isEmptyValue(value: any) {
            return value == null || value === "";
        }

        private static isValueChanged(prevValue: any, newValue: any) {
            const normalizeValue = (value: any) => {
                if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
                    return null;
                }

                if (Array.isArray(value)) {
                    return [...value].sort();
                }

                return value;
            };

            return !isEqual(normalizeValue(prevValue), normalizeValue(newValue));
        }
    };
};
